import { useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
  type Firestore,
} from "firebase/firestore";
import { deleteObject, ref, type FirebaseStorage } from "firebase/storage";
import { splitSerialParts, normalizeSerialKey } from "../components/editModalFunctions";

type DeleteItem = {
  id: string;
  deletePending?: boolean;
  completed?: boolean;
  lockedBy?: string | null;
  invoiceReportId?: string | null;
  markedForInvoice?: boolean;
  photos?: Record<string, string | null>;
  serial?: string | null;
  serialBaseKey?: string | null;
  serialVisit?: number | null;
  orderNumber?: string | null;
};

type Params = {
  db: Firestore;
  storage: FirebaseStorage;
  items: DeleteItem[];
  currentUserString: () => string | null;
  setDeleteConfirmText: (val: string) => void;
  setShowDeleteModal: (val: boolean) => void;
  deleteConfirmText: string;
  refreshHomeList: () => Promise<void>;
};

export function useDeleteActions({
  db,
  storage,
  items,
  currentUserString,
  setDeleteConfirmText,
  setShowDeleteModal,
  deleteConfirmText,
  refreshHomeList,
}: Params) {
  const pendingDeletableItems = useMemo<DeleteItem[]>(() => {
    const me = currentUserString();
    return items
      .filter((it) =>
        it.deletePending &&
        (!it.completed) &&
        (!it.lockedBy || it.lockedBy === me) &&
        ((it as any).deleteMarkedBy ? (it as any).deleteMarkedBy === me : true));
  }, [items, currentUserString]);
  const pendingDeletableIds = useMemo(
    () => pendingDeletableItems.map((it) => it.id),
    [pendingDeletableItems]
  );
  const [deleteModalItems, setDeleteModalItems] = useState<DeleteItem[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);

  const openDeleteModal = (): void => {
    if (pendingDeletableItems.length === 0) return;
    setDeleteModalItems(pendingDeletableItems.map((it) => ({ ...it })));
    setDeleteConfirmText("");
    setShowDeleteModal(true);
  };

  const cancelDeleteModal = (): void => {
    setShowDeleteModal(false);
    setDeleteConfirmText("");
    setDeleteModalItems([]);
  };

  const deletePendingMarked = async (): Promise<void> => {
    const ids = deleteModalItems.map((it) => it.id);
    if (ids.length === 0) {
      alert("Inget att radera.");
      return;
    }

    type DeletionItem = { id: string; baseKey: string; visitNum: number };

    const deletions: DeletionItem[] = ids
      .map((id) => {
        const it = deleteModalItems.find((x) => x.id === id);
        if (!it) return null;
        const { base: baseFromSerial, visit: visitFromSerial } = splitSerialParts(String(it.serial || ""));
        const baseKey = (it as any).serialBaseKey ?? normalizeSerialKey(baseFromSerial || "");
        const visitNum = Number((it as any).serialVisit ?? visitFromSerial ?? 1);
        return { id, baseKey, visitNum };
      })
      .filter(Boolean) as DeletionItem[];

    deletions.sort((a, b) => {
      if (a.baseKey < b.baseKey) return -1;
      if (a.baseKey > b.baseKey) return 1;
      return b.visitNum - a.visitNum;
    });

    const photoMap: Record<string, string[]> = {};
    for (const { id } of deletions) {
      const it = deleteModalItems.find(i => i.id === id);
      if (it?.photos) {
        photoMap[id] = Object.values(it.photos).filter(Boolean) as string[];
      }
    }

    let ok = 0;
    for (const { id } of deletions) {
      try {
        await runTransaction(db, async (tx) => {
          const itemRef = doc(db, "itInventory", id);
          const snap = await tx.get(itemRef);
          if (!snap.exists()) return;

          const cur = snap.data() as any;
          if (cur.deletePending !== true) {
            throw new Error("Posten är inte längre markerad för radering.");
          }
          if (cur.completed) throw new Error("Kan inte radera “ enheten är markerad som färdig.");
          if (cur.invoiceReportId) throw new Error("Kan inte radera “ enheten är fakturerad.");
          if (cur.markedForInvoice === true && !cur.invoiceReportId) {
            throw new Error("Kan inte radera “ enheten är markerad för fakturering.");
          }
          if (cur.lockedBy && cur.lockedBy !== currentUserString()) {
            throw new Error(`Kan inte radera “ posten redigeras av ${cur.lockedBy}.`);
          }
          if (cur.deleteMarkedBy && cur.deleteMarkedBy !== currentUserString()) {
            throw new Error(`Endast ${cur.deleteMarkedBy} får radera denna post.`);
          }

          const { base: baseFromSerial, visit: visitFromSerial } = splitSerialParts(String(cur.serial || ""));
          const baseKey: string = cur.serialBaseKey || normalizeSerialKey(baseFromSerial || "");
          const visitNum = Number(cur.serialVisit || visitFromSerial || 1);

          if (baseKey) {
            const idxRef = doc(collection(db, "serialIndex"), baseKey);
            const idxSnap = await tx.get(idxRef);
            if (idxSnap.exists()) {
              const curIdx = idxSnap.data() as any;
              const nextActive = Math.max(0, Number(curIdx.active ?? 0) - 1);
              tx.update(idxRef, {
                active: nextActive,
                lastSeen: serverTimestamp(),
                lastItemId: itemRef.id,
              });
            }
          }

          tx.delete(itemRef);
        });

        const urls = photoMap[id] || [];
        for (const url of urls) {
          try {
            const path = new URL(url).pathname.split("/o/")[1].split("?")[0];
            const storageRefObj = ref(storage, decodeURIComponent(path));
            await deleteObject(storageRefObj);
          } catch (err: any) {
            if (err?.code !== "storage/object-not-found") {
              console.debug("Delete (by URL) misslyckades:", err?.code || err);
            }
          }
        }

        ok += 1;
      } catch (e: any) {
        console.warn(`Kunde inte radera ${id}:`, e?.message || e);
      }
    }

    alert(`Raderade ${ok} av ${deletions.length} markerad(e) enhet(er).`);
    await refreshHomeList();
  };

  const confirmDeleteModal = async (): Promise<void> => {
    if (deleteConfirmText !== "DELETE" || isDeletingRef.current) return;
    isDeletingRef.current = true;
    setIsDeleting(true);
    try {
      await deletePendingMarked();
      setShowDeleteModal(false);
      setDeleteConfirmText("");
      setDeleteModalItems([]);
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  };

  return {
    pendingDeletableIds,
    deleteModalItems,
    isDeleting,
    openDeleteModal,
    cancelDeleteModal,
    confirmDeleteModal,
    deletePendingMarked,
    getDeleteToggleMeta: (
      item: DeleteItem,
      state: "open" | "ready" | "invoiced",
      lockedByOther: boolean,
      lockHint: string
    ) => {
      const me = currentUserString();
      const markedByOther = !!item.deletePending && !!(item as any).deleteMarkedBy && (item as any).deleteMarkedBy !== me;
      const disabled = state !== "open" || lockedByOther || markedByOther;
      const title = lockedByOther
        ? lockHint
        : markedByOther
          ? `Markerad för radering av ${(item as any).deleteMarkedBy}`
          : state !== "open"
            ? "Kan inte raderas om enheten är färdig/fakturerad"
            : item.deletePending
              ? "Avmarkera radering"
              : "Markera för radering";
      return { disabled, title };
    },
  };
}
